import { Alert, AlertTitle, Box, Button, List, Pagination, Typography, Collapse, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FieldArray, useFormikContext } from "formik";
import React, { useContext, useMemo, useState, useEffect } from "react";
import { PromoMediaManagerDrawer } from "../components/MediaManager.component";
import PromoItemsService from "../services/promoItems.service";
import SpendCategoriesTreeView from "modules/MasterData/components/SpendCategoriesTreeView.component";
import { PSL } from "../domains/psl.domain";
import { useStaticData } from "modules/MasterData/service/staticData.hooks";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { stepContext } from "modules/Forms/components/formikStepperProvider.component";
import { UserService } from "modules/User/service/user.service";
import { UserInteractionType } from "modules/User/service/userInteractionType";
import PromoItemRow from "../components/PromoItemRow.component";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StepperDirection } from "modules/Forms/components/formikStepper.component";
import { useActiveUser } from "modules/User/activeUser";

export const CreateStepTwoContainer = (props: {
  defaultSpendCategories: { l1: any; l2: any; l3: any };
  companyCodes: any;
}) => {
  const { defaultSpendCategories } = props;
  const staticData = useStaticData();
  const activeUser = useActiveUser();
  const { values, setFieldValue, getFieldMeta } =
    useFormikContext<any>();
  const allMaterials = values.materials.filter((m) => !m.deleted);
  const [allCollapsed, setAllCollapsed] = useState(allMaterials.length > 5);
  const {
    readOnly,
    currentStep,
    setCurrentStep,
    displayedCurrentStep,
    setDisplayedCurrentStep,
    setDirection,
  } = useContext(stepContext);
  const [selectedPromoItem, setSelectedPromoItem] = useState(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const pslId = values?.vendor?.pslId;
  const isEditMode = !!pslId;

  const hasMaterialsTotal = values.materialsTotal !== undefined;

  const totalMaterials = useMemo(() => {
    if (!isEditMode) {
      return allMaterials.length;
    }

    const backendTotal = values.materialsTotal ?? 0;

    // current page visible items (after add/delete)
    const currentPageCount = values.materials.filter((m) => !m.deleted).length;

    const expectedPageCount = Math.min(pageSize, backendTotal);

    const diff = currentPageCount - expectedPageCount;

    return backendTotal + diff;
  }, [isEditMode, values.materials, values.materialsTotal, pageSize]);

  const totalPages = hasMaterialsTotal ? Math.ceil(totalMaterials / pageSize) : 0;
  const needsPagination = isEditMode && hasMaterialsTotal && totalPages > 1;

  // Fetch on first visit (to determine total) or when pagination is active
  const shouldFetchMaterials = isEditMode && (!hasMaterialsTotal || needsPagination);

  const { data: paginatedMaterialsData, isLoading: isLoadingMaterials } = useQuery({
    queryKey: ["materialsForPslEntry", pslId, currentPage, pageSize],
    queryFn: () =>
      PromoItemsService.fetchMaterialsForPslEntry({
        id: pslId?.toString(),
        includeDeleted: false,
        skip: (currentPage - 1) * pageSize,
        take: pageSize,
      }),
    enabled: shouldFetchMaterials, // Fetch when pagination is needed (all pages)
    staleTime: 0,
    gcTime: 0,
  });
  const queryClient = useQueryClient();
  const [animatedIndex, setAnimatedIndex] = useState<number | null>(null);
  // const [duplicatedIndexes, setDuplicatedIndexes] = useState<number[]>([]);
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [isBackTriggered, setIsBackTriggered] = useState(false);
  const [pendingBack, setPendingBack] = useState(false);
  useEffect(() => {
    if (!isEditMode) return;
    setFieldValue("materialsPage", currentPage, false);
    setFieldValue("materialsTotalPages", totalPages, false);
    setFieldValue("materialsNeedsPagination", needsPagination, false);
  }, [isEditMode, currentPage, totalPages, needsPagination, setFieldValue]);

  useEffect(() => {
    if (paginatedMaterialsData?.total !== undefined && isEditMode) {
      // Update materialsTotal in Formik if it's different
      if (values.materialsTotal !== paginatedMaterialsData.total) {
        setFieldValue("materialsTotal", paginatedMaterialsData.total, false);
      }
    }
  }, [paginatedMaterialsData?.total, isEditMode, values.materialsTotal, setFieldValue]);

  useEffect(() => {
    if (!isEditMode || !shouldFetchMaterials || !paginatedMaterialsData?.data || isLoadingMaterials) {
      return;
    }

    // Add hasTaxonomy property to materials fetched from server
    const materialsWithTaxonomy = paginatedMaterialsData.data.map((material: any) => ({
      ...material,
      hasTaxonomy:
        staticData.taxonomy.filter(
          (cat) => cat.spendCategoryL3?.id === (typeof material.spendCat3Id === 'string' ? parseInt(material.spendCat3Id, 10) : material.spendCat3Id)
        ).length > 0,
      priceRanges: Array.isArray(material.priceRanges) && material.priceRanges.length > 0
        ? material.priceRanges
        : [{ quantity: material.minimumOrderQuantity || 0, price: 0, pricePer: 1 }],
      mediaFiles: material.mediaFiles ?? [],
    }));
    setFieldValue("materials", materialsWithTaxonomy, true); // true to trigger validation
    setAllCollapsed((paginatedMaterialsData.total ?? paginatedMaterialsData.data.length) > 5);
  }, [isEditMode, shouldFetchMaterials, paginatedMaterialsData, isLoadingMaterials, setFieldValue, staticData.taxonomy]);

  const { mutateAsync: updateMaterialsBulk, isPending: isSavingMaterials } = useMutation<
    any,
    any,
    any[]
  >({
    mutationKey: ["updateMaterialsForPslEntryBulk", pslId, currentPage],
    mutationFn: async (materials) =>
      await PromoItemsService.updateMaterialsForPslEntryBulk(materials),
    onSuccess: () => {
      toast.success(
        `Materials ${startIndex}-${endIndex} of catalogue ${pslId} updated successfully!`
      );

      queryClient.invalidateQueries({
        queryKey: ["materialsForPslEntry"],
      });
    },
  });

  const { mutateAsync: uploadPromoMedia, isPending } = useMutation<
    any,
    any,
    any
  >({
    mutationKey: ["uploadPromoMedia"],
    mutationFn: async (data) =>
      await PromoItemsService.uploadPromoMaterialsMedia(data),
  });

  const { mutateAsync: uploadPromoMediaFromLink } = useMutation<any, any, any>({
    mutationKey: ["uploadPromoMediaFromLink"],
    mutationFn: async ({ link }) =>
      await PromoItemsService.uploadPromoMediaFromLink({ link }),
  });

  const locationCompanyCodes = useMemo(() => {
    return values.vendor.markets
      .map((market) => props.companyCodes[market.id])
      .concat(values.vendor.companyCodes.map((it) => it.code))
      .join(",");
  }, [props.companyCodes, values]);

  const pslClass = useMemo(() => {
    return new PSL(values.vendor);
  }, [values]);
  const isChildPsl = pslClass.isChildPsl();

  const isLocalVendor =
    values.vendor.pslIdWithPrefix?.startsWith("LP");

  // const allowCpEditableMedia =
  //   isEditMode &&
  //   isChildPsl &&
  //   !isLocalVendor &&
  //   (
  //     activeUser.hasAboveMarketRole() ||
  //     activeUser.isAtMarket()
  //   );
const allowCpEditableMedia =
  !isEditMode || // New catalogue
  (
    isChildPsl &&
    (
      isLocalVendor || // Local catalogue
      activeUser.hasAboveMarketRole() ||
      activeUser.isAtMarket()
    )
  );
  // Get materials for current page
  const paginatedMaterials = useMemo(() => {
    if (isEditMode) {
      // materials already set from current page fetch
      return allMaterials;
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allMaterials.slice(startIndex, endIndex);
  }, [isEditMode, allMaterials, currentPage, pageSize]);

  const startIndex = (currentPage - 1) * pageSize + 1;
  const currentPageCount = values.materials.filter((m) => !m.deleted).length;

  const endIndex = Math.min(
    startIndex + currentPageCount - 1,
    totalMaterials
  );

  // Handle page change with debouncing for better performance
  const handlePageChange = (event, page) => {
    if (hasUnsavedChanges) {
      setPendingPage(page);
    } else {
      setCurrentPage(page);
    }
  };

  const getEmptyRow = async (
    l1Id,
    l1Title,
    l1Code,
    l2Id,
    l2Title,
    l2Code,
    l3Id,
    l3Title,
    l3Code,
    materialId,
    mCode,
    mDesc,
    mLongDesc
  ) => {
    return {
      spendCat1Id: l1Id,
      spendCat1DisplayValue: l1Title,
      spendCat1Code: l1Code,
      spendCat2Id: l2Id,
      spendCat2DisplayValue: l2Title,
      spendCat2Code: l2Code,
      spendCat3Id: l3Id,
      spendCat3DisplayValue: l3Title,
      spendCat3Code: l3Code,
      hasTaxonomy:
        staticData.taxonomy.filter((cat) => cat.spendCategoryL3?.id === (typeof l3Id === 'string' ? parseInt(l3Id, 10) : l3Id))
          .length > 0, // eslint-disable-line
      materialId: materialId,
      parentMaterialId: null,
      languageId: 47,
      languageTag: "English",
      keyWords: [],
      unitOfMeasure: "EA",
      itemDescription: mLongDesc ? mLongDesc : "",
      shortDescription: mDesc ? mDesc : "",
      unitPrice: null,
      pricePer: null,
      supplierLeadTime: null,
      matGrp: "",
      materialNumber: mCode ? mCode : "",
      materialName: mDesc ? mDesc : "",
      supplierPartId: "",
      manufacturerPartNumber: "",
      manufacturerName: "",
      deleted: false,
      minimumOrderQuantity: null,
      currencyCode: "USD",
      waysOfBuying: [1, 2, 3],
      marketContactName: null,
      mediaFiles: [],
      plants: [],
      priceRanges: [{ quantity: 0, price: 0, pricePer: 1 }],
    };
  };
  const selectedItem =
    deleteItemIndex !== null ? values.materials[deleteItemIndex] : null;
  const changedItems = values.materials.filter(
    (m) => m.materialId === null || m.deleted
  );

  const hasValidationErrors = () => {
    return values.materials.some((item) => {
      if (item.deleted) return false;
      return (
        !item.shortDescription ||
        !item.materialNumber ||
        !item.itemDescription ||
        !item.languageId ||
        item.minimumOrderQuantity === null ||
        item.minimumOrderQuantity === undefined ||
        !item.unitOfMeasure ||
        !item.keyWords ||
        item.keyWords.length === 0
      );
    });
  };
  useEffect(() => {
    if (values?._goBack) {
      setPendingPage(-1); // trigger dialog
      setFieldValue("_goBack", false);
    }
  }, [values?._goBack]);
  return (
    <>
      {/* {(isChildPsl || values.campaignRelated) && (
        <Alert severity="info">
          <AlertTitle>
            Please note that some fields are locked for editing!
          </AlertTitle>
          <Box>
            The catalog you've opened{" "}
            {isChildPsl ? "is a child" : "was created from a campaign"} so some
            of the values are locked as they are inherited from{" "}
            {isChildPsl ? "the parent catalog" : "the related campaign"}.
          </Box>
        </Alert>
      )} */}

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 3, mb: 3 }}>
        {
          // isChildPsl || values.campaignRelated || 
          readOnly ? (
            <></>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <SpendCategoriesTreeView
                l1Id={values.vendor.spendCat1Id}
                addRows={async (
                  data: {
                    l1Id: string;
                    l1Title: string;
                    l1Code: string;
                    l2Id: string;
                    l2Title: string;
                    l2Code: string;
                    l3Id: string;
                    l3Title: string;
                    l3Code: string;
                    mCode: string;
                    mDesc: string;
                    mLongDesc: string;
                  }[]
                ) => {
                  // const id = () => "a" + Math.floor(Math.random() * 100000000);
                  const newData = await Promise.all(
                    data.map(async (d) => {
                      return await getEmptyRow(
                        d.l1Id,
                        d.l1Title,
                        d.l1Code,
                        d.l2Id,
                        d.l2Title,
                        d.l2Code,
                        d.l3Id,
                        d.l3Title,
                        d.l3Code,
                        null, //id(),
                        d.mCode,
                        d.mDesc,
                        d.mLongDesc
                      );
                    })
                  );
                  setFieldValue("materials", [...values.materials, ...newData]);
                  queryClient.invalidateQueries({
                    queryKey: ["materialsForPslEntry"],
                  });
                }}
              />
            </Box>
          )}
        <Button
          sx={{ ml: "auto" }}
          onClick={() => setAllCollapsed(!allCollapsed)}
          startIcon={allCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        >
          {allCollapsed ? "Expand all" : "Collapse all"}
        </Button>
      </Box>
      <List>
        <FieldArray name="materials">
          {(arrayHelpers) => {
            return (
              <>
                <Dialog
                  open={validationDialogOpen}
                  onClose={() => setValidationDialogOpen(false)}
                >
                  <DialogTitle>Validation Error</DialogTitle>

                  <DialogContent>
                    <Typography>
                      Please fill all mandatory fields before saving.
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      {values.materials
                        .filter((item) => {
                          if (item.deleted) return false;
                          return (
                            !item.shortDescription ||
                            !item.materialNumber ||
                            !item.itemDescription ||
                            !item.languageId ||
                            item.minimumOrderQuantity === null ||
                            item.minimumOrderQuantity === undefined ||
                            !item.unitOfMeasure ||
                            !item.keyWords ||
                            item.keyWords.length === 0
                          );
                        })
                        .map((item, idx) => (
                          <Typography key={idx} variant="body2">
                            • {item.materialName || "New Item"} (Material ID{" "}
                            {item.materialId ? `#${item.materialId}` : "#New Item"})
                          </Typography>
                        ))}
                    </Box>
                  </DialogContent>

                  <DialogActions>
                    <Button onClick={() => setValidationDialogOpen(false)}>
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
                <Dialog open={deleteItemIndex !== null} onClose={() => setDeleteItemIndex(null)}>
                  <DialogTitle>Confirm Delete</DialogTitle>

                  <DialogContent>
                    {deleteItemIndex !== null && (
                      <Box>
                        <Typography>
                          <b>Material:</b> {selectedItem?.materialName}
                        </Typography>

                        <Typography>
                          <b>Code:</b> {selectedItem?.materialNumber}
                        </Typography>

                        <Typography>
                          <b>Material ID:</b>{" "}
                          {selectedItem?.materialId
                            ? `#${selectedItem.materialId}`
                            : "#New Item"}
                        </Typography>
                      </Box>
                    )}
                  </DialogContent>

                  <DialogActions>
                    <Button onClick={() => setDeleteItemIndex(null)}>Cancel</Button>
                    <Button
                      color="error"
                      onClick={() => {
                        const item = values.materials[deleteItemIndex];

                        if (item.materialId === null) {
                          arrayHelpers.remove(deleteItemIndex);
                        } else {
                          arrayHelpers.replace(deleteItemIndex, {
                            ...item,
                            deleted: true,
                          });
                        }
                        setHasUnsavedChanges(true);
                        setDeleteItemIndex(null);
                      }}
                    >
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>
                {/* <Dialog open={pendingPage !== null}> */}
                <Dialog open={pendingPage !== null || pendingBack}>
                  <DialogTitle>Unsaved Changes</DialogTitle>

                  <DialogContent>

                    <Typography sx={{ mb: 1 }}>
                      You have unsaved changes. Do you want to continue without saving?
                    </Typography>

                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {changedItems.map((item, idx) => (
                        <Typography key={idx} variant="body2">
                          • {item.materialName} (Material ID {item.materialId ? `#${item.materialId}` : "#New Item"})
                        </Typography>
                      ))}
                    </Box>
                  </DialogContent>

                  <DialogActions>
                    {/* <Button onClick={() => setPendingPage(null)}>Cancel</Button> */}
                    <Button
                      onClick={() => {
                        setPendingPage(null);
                        setIsBackTriggered(false);
                      }}
                    >
                      Cancel
                    </Button>

                    {/* <Button
                      color="warning"
                      onClick={() => {
                        setCurrentPage(pendingPage!);
                        setPendingPage(null);
                        //setHasUnsavedChanges(false);
                      }}
                    >
                      Continue
                    </Button> */}
                    <Button
                      color="warning"
                      onClick={() => {
                        if (pendingPage === -1) {
                          // 🔥 BACK FLOW
                          setPendingPage(null);

                          setDirection(StepperDirection.Left);
                          setCurrentStep(currentStep - 1);
                          setDisplayedCurrentStep(displayedCurrentStep - 1);
                        } else {
                          setCurrentPage(pendingPage!);
                          setPendingPage(null);
                        }
                      }}
                    >
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
                <PromoMediaManagerDrawer
                  readOnly={readOnly}
                  isChildPsl={isChildPsl}
                  allowCpEditableMedia={allowCpEditableMedia}
                  campaignState={values.vendor.pslStatus}
                  promoCategoryId={
                    values.materials[selectedPromoItem]?.promoCategoryId
                  }
                  materialId={values.materials[selectedPromoItem]?.materialId}
                  open={selectedPromoItem !== null}
                  onClose={() => setSelectedPromoItem(null)}
                  images={
                    selectedPromoItem !== null
                      ? values.materials[selectedPromoItem].mediaFiles?.filter(
                        (m) => m.isQuality === false
                      )
                      : []
                  }
                  qualityDocs={
                    selectedPromoItem !== null
                      ? values.materials[selectedPromoItem].mediaFiles?.filter(
                        (m) => m.isQuality === true
                      )
                      : []
                  }
                  parentMediaFiles={
                    selectedPromoItem !== null
                      ? values.materials[selectedPromoItem].parentMediaFiles?.filter(
                        (m) => m.isQuality === false
                      )
                      : []
                  }
                  parentQualityDocs={
                    selectedPromoItem !== null
                      ? values.materials[selectedPromoItem].parentMediaFiles?.filter(
                        (m) => m.isQuality === true
                      )
                      : []
                  }
                  loadingQualityDocs={isPending}
                  deletingQualityDocs={false}
                  onUploadFromUrl={(link) => {
                    UserService.logUserInteractionData(UserInteractionType.AddNSCMediaPhotoMaterial);
                    uploadPromoMediaFromLink({
                      link,
                      materialId:
                        values.materials[selectedPromoItem]?.materialId,
                    })
                      .then((response) => {
                        arrayHelpers.replace(selectedPromoItem, {
                          ...arrayHelpers.form.values.materials[
                          selectedPromoItem
                          ],
                          mediaFiles:
                            arrayHelpers.form.values.materials[
                              selectedPromoItem
                            ].mediaFiles.concat(response),
                        });
                      })
                      .catch((err) => {
                        console.error(err);
                      });
                  }}
                  onUpload={(files, data) => {
                    UserService.logUserInteractionData(UserInteractionType.AddNSCMediaPhotoMaterial);
                    uploadPromoMedia({
                      file: files[0],
                      materialId:
                        values.materials[selectedPromoItem]?.materialId,
                      ...data,
                    })
                      .then((response) => {
                        arrayHelpers.replace(selectedPromoItem, {
                          ...arrayHelpers.form.values.materials[
                          selectedPromoItem
                          ],
                          mediaFiles:
                            arrayHelpers.form.values.materials[
                              selectedPromoItem
                            ].mediaFiles.concat(response),
                        });
                      })
                      .catch((err) => {
                        console.error(err);
                      });
                  }}
                  onDelete={(mediaId) => {
                    UserService.logUserInteractionData(UserInteractionType.DeleteNSCMediaPhotoMaterial);
                    // deletePromoMedia(mediaId).then((deletedFileId) => {
                    arrayHelpers.replace(selectedPromoItem, {
                      ...arrayHelpers.form.values.materials[
                      selectedPromoItem
                      ],
                      mediaFiles: arrayHelpers.form.values.materials[
                        selectedPromoItem
                      ].mediaFiles.filter((f) => f.fileId !== mediaId),
                    });
                    // })
                    ;
                  }}
                />
                {/* Materials count and pagination info */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {isLoadingMaterials || !hasMaterialsTotal
                      ? "Loading materials..."
                      : `Showing ${startIndex}-${endIndex} of ${totalMaterials} materials`}
                  </Typography>

                  {/* Top pagination controls */}
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      disabled={isLoadingMaterials || isSavingMaterials}
                    />
                  )}
                </Box>

                {/* Render paginated materials */}
                {!isLoadingMaterials && paginatedMaterials.map((promoItem, pageIndex) => {
                  const actualIndex = values.materials.findIndex(
                    (m) => m === promoItem
                  );
                  return (
                    <Collapse in timeout={400} appear>
                      <Box
                        sx={{
                          borderRadius: "12px",
                          marginBottom: "8px",
                          padding: "2px",
                          transition: "all 0.3s ease",

                          backgroundColor:
                            animatedIndex === actualIndex ? "#eef2ff" : "transparent",

                          boxShadow:
                            animatedIndex === actualIndex
                              ? "0px 8px 30px rgba(30, 64, 175, 0.45)"
                              : "0px 1px 3px rgba(0,0,0,0.1)",

                          transform:
                            animatedIndex === actualIndex ? "scale(1.01)" : "scale(1)",

                          // ✅ HOVER EFFECT (ADD THIS)
                          "&:hover": {
                            transform: "scale(1.01)",
                            boxShadow: "0px 6px 20px rgba(0,0,0,0.2)",
                            backgroundColor:
                              animatedIndex === actualIndex ? "#eef2ff" : "#f9fafb",
                          },
                        }}


                      >
                        <PromoItemRow
                          key={`${promoItem.materialId || pageIndex}-${actualIndex}`}
                          readOnly={readOnly}
                          collapsed={allCollapsed}
                          defaultSpendCategories={defaultSpendCategories}
                          locationCompanyCodes={locationCompanyCodes}
                          promoItem={promoItem}
                          promoItemIndex={actualIndex}
                          isEditMode={isEditMode}
                          // onDuplicate={(promoItem) => {
                          //   arrayHelpers.push({
                          //     ...promoItem,
                          //     materialId: null,
                          //   });
                          // }}
                          onDuplicate={(promoItem) => {
                            setHasUnsavedChanges(true);
                            const index = actualIndex;
                            const newIndex = index + 1;

                            arrayHelpers.insert(newIndex, {
                              ...promoItem,
                              materialId: null,
                            });
                            // setDuplicatedIndexes((prev) => [...prev, newIndex]);
                            // trigger animation
                            setAnimatedIndex(newIndex);

                            setTimeout(() => {
                              setAnimatedIndex(null);
                            }, 6000);
                          }}
                          // onRemove={(promoItem) => {
                          //   if (promoItem.materialId === null) {
                          //     arrayHelpers.remove(actualIndex);
                          //   } else {
                          //     arrayHelpers.replace(actualIndex, {
                          //       ...promoItem,
                          //       deleted: true,
                          //     });
                          //   }
                          // }}
                          onRemove={() => {
                            setDeleteItemIndex(actualIndex);
                          }}
                          onManageMedia={() => {
                            setSelectedPromoItem(actualIndex);
                          }}
                        />
                      </Box>
                    </Collapse>
                  );
                })}

                {/* Bottom pagination controls */}
                {totalPages > 1 && (
                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                        disabled={isLoadingMaterials || isSavingMaterials}
                      />
                    </Box>
                    {/* <Button
                      variant="contained"
                      size="large"
                      disabled={readOnly || isSavingMaterials || isLoadingMaterials}
                      //   onClick={async () => {
                      //     if (!pslId) return;
                      //     const materialsToSave = values.materials.map((material) => ({
                      //       ...material,
                      //       pslId,
                      //     }));
                      //     await updateMaterialsBulk(materialsToSave);
                      //     //setDuplicatedIndexes([]);
                      //     setHasUnsavedChanges(false);
                      //   }}
                      // >
                      onClick={async () => {
                        if (!pslId) return;

                        if (hasValidationErrors()) {
                          setValidationDialogOpen(true);
                          return; // 🚫 STOP API CALL
                        }

                        const materialsToSave = values.materials.map((material) => ({
                          ...material,
                          pslId,
                        }));

                        await updateMaterialsBulk(materialsToSave);

                       
                      }}
                    >
                      {isSavingMaterials ? "Saving..." : "Save"}
                    </Button> */}
                    <Button
                      variant="contained"
                      size="large"
                      disabled={readOnly || isSavingMaterials || isLoadingMaterials}
                      onClick={async () => {
                        if (!pslId) return;

                        // mark that user attempted changes
                        setHasUnsavedChanges(true);

                        // 🚫 validation check
                        if (hasValidationErrors()) {
                          setValidationDialogOpen(true);
                          return;
                        }

                        const materialsToSave = values.materials.map((material) => ({
                          ...material,
                          pslId,
                        }));

                        await updateMaterialsBulk(materialsToSave);

                        // ✅ reset unsaved state after successful save
                        setHasUnsavedChanges(false);
                      }}
                    >
                      {isSavingMaterials ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                )}

              </>
            );
          }}
        </FieldArray>
        {typeof getFieldMeta("materials").error === "string" && (
          <Alert severity="error">
            <AlertTitle>
              {getFieldMeta("materials").error?.toString()}
            </AlertTitle>
          </Alert>
        )}
      </List>
    </>
  );
};
export default CreateStepTwoContainer;
